/** Service Requirements tab for the Customer Workspace (Demo Mode only). Editable view of the
 * catering/service requirements captured during onboarding (see
 * `../../OnboardingDetail/index.tsx`'s "Service requirements" tab and
 * `mocks/data/customerManagementData.ts`'s `CustomerServiceRequirements`) - duplicated here so
 * the same data stays visible and editable once a customer has gone live, not just during
 * onboarding. Bound to the parent workspace's edit draft (see `../index.tsx`). */

import { Box, Grid, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import { CustomerRecord, CustomerServiceRequirements, getServiceRequirements } from '../../../../mocks/data/customerManagementData';
import EditableStringList from '../../components/EditableStringList';

interface ServiceRequirementsTabProps {
  customer: CustomerRecord;
  onChange: (updater: (prev: CustomerRecord) => CustomerRecord) => void;
}

const ServiceRequirementsTab = ({ customer, onChange }: ServiceRequirementsTabProps) => {
  const { t } = useTranslation('common');
  const requirements = getServiceRequirements(customer);

  const updateRequirements = (patch: Partial<CustomerServiceRequirements>) => {
    onChange((prev) => ({ ...prev, serviceRequirements: { ...getServiceRequirements(prev), ...patch } }));
  };

  return (
    <Section style={{ p: 5 }}>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}
          md={6}>
          <EditableStringList
            label={t('onboardingDetailUserTypes', 'Type of users')}
            items={requirements.userTypes}
            onChange={(userTypes) => updateRequirements({ userTypes })}
            addLabel={t('onboardingDetailAddUserType', 'Add user type')}
            placeholder={t('onboardingDetailUserTypePlaceholder', 'e.g. Students, staff, visitors')}
          />
          <Box sx={{ mt: 3 }}>
            <EditableStringList
              label={t('onboardingDetailServiceTimes', 'Service times')}
              items={requirements.serviceTimes}
              onChange={(serviceTimes) => updateRequirements({ serviceTimes })}
              addLabel={t('onboardingDetailAddServiceTime', 'Add service time')}
              placeholder={t('onboardingDetailServiceTimePlaceholder', 'e.g. Breakfast 7-9am')}
            />
          </Box>
        </Grid>

        <Grid item
          xs={12}
          md={6}>
          <EditableStringList
            label={t('onboardingDetailMenus', 'Menus')}
            items={requirements.menus}
            onChange={(menus) => updateRequirements({ menus })}
            addLabel={t('onboardingDetailAddMenu', 'Add menu')}
            placeholder={t('onboardingDetailMenuPlaceholder', 'e.g. All-day dining menu')}
          />
          <Box sx={{ mt: 3 }}>
            <EditableStringList
              label={t('onboardingDetailDietaryRequirements', 'Dietary requirements')}
              items={requirements.dietaryRequirements}
              onChange={(dietaryRequirements) => updateRequirements({ dietaryRequirements })}
              addLabel={t('onboardingDetailAddDietaryRequirement', 'Add dietary requirement')}
              placeholder={t('onboardingDetailDietaryRequirementPlaceholder', 'e.g. Vegan, halal, gluten-free')}
            />
          </Box>
          <Box sx={{ mt: 3 }}>
            <EditableStringList
              label={t('onboardingDetailAllergens', 'Allergens')}
              items={requirements.allergens}
              onChange={(allergens) => updateRequirements({ allergens })}
              addLabel={t('onboardingDetailAddAllergen', 'Add allergen')}
              placeholder={t('onboardingDetailAllergenPlaceholder', 'e.g. Nuts, dairy, shellfish')}
            />
          </Box>
        </Grid>

        <Grid item
          xs={12}
          md={6}>
          <Typography variant="textSM"
            component="div"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('onboardingDetailHospitalityBudget', 'Hospitality & budget')}</Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}>
              <TextField
                label={t('onboardingDetailHospitalityNeeds', 'Hospitality needs')}
                value={requirements.hospitalityNeeds}
                onChange={(e) => updateRequirements({ hospitalityNeeds: e.target.value })}
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
            </Grid>
            <Grid item
              xs={6}>
              <TextField
                label={t('onboardingDetailBudget', 'Budget')}
                value={requirements.budget}
                onChange={(e) => updateRequirements({ budget: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={6}>
              <TextField
                label={t('onboardingDetailPricingModel', 'Pricing model')}
                value={requirements.pricingModel}
                onChange={(e) => updateRequirements({ pricingModel: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={6}>
          <EditableStringList
            label={t('onboardingDetailComplianceExpectations', 'Compliance expectations')}
            items={requirements.complianceExpectations}
            onChange={(complianceExpectations) => updateRequirements({ complianceExpectations })}
            addLabel={t('onboardingDetailAddComplianceExpectation', 'Add compliance expectation')}
            placeholder={t('onboardingDetailComplianceExpectationPlaceholder', 'e.g. HACCP certification required')}
          />
          <Box sx={{ mt: 3 }}>
            <EditableStringList
              label={t('onboardingDetailReportingRequirements', 'Reporting requirements')}
              items={requirements.reportingRequirements}
              onChange={(reportingRequirements) => updateRequirements({ reportingRequirements })}
              addLabel={t('onboardingDetailAddReportingRequirement', 'Add reporting requirement')}
              placeholder={t('onboardingDetailReportingRequirementPlaceholder', 'e.g. Monthly spend report')}
            />
          </Box>
        </Grid>
      </Grid>
    </Section>
  );
};

export default ServiceRequirementsTab;
